// Client for the public market-data Worker API. Only ever fetches public
// market data — no user data is ever sent.

import type { SeriesDetail, SeriesSignal } from "./signals/timing";
import type { EnblocDigest, EnblocInsight } from "./signals/enbloc";

// Configured at build time via Vite env (VITE_API_BASE). On local dev an empty
// base is correct (Vite proxies "/api" to the local Worker). But if a production
// build ever ships with VITE_API_BASE unset, an empty base makes every "/api/*"
// call hit the Pages origin (which has no API) and silently blanks all market
// data. Guard against that: when served from *.pages.dev with no configured
// base, fall back to the deployed Worker URL (a public workers.dev host, not a
// secret).
const PROD_API_FALLBACK = "https://asset-feasibility-api.nicholas-yeo.workers.dev";
const ENV_API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";
const API_BASE = ENV_API_BASE
  || (typeof location !== "undefined" && location.hostname.endsWith(".pages.dev") ? PROD_API_FALLBACK : "");

export async function fetchSignals(): Promise<SeriesSignal[]> {
  const res = await fetch(`${API_BASE}/api/signals`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  const json = (await res.json()) as { series: SeriesSignal[] };
  return json.series;
}

export async function fetchSeries(id: string): Promise<SeriesDetail> {
  const res = await fetch(`${API_BASE}/api/series/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  return (await res.json()) as SeriesDetail;
}

export interface Rates {
  sora: number | null;
  asOf: string | null;
  indicativeMortgageRatePct: number | null;
  carLoanFlatRatePct: number | null;
  source: string;
}

// En-bloc insight for one development. Opt-in: the browser only calls this when
// the user taps "Check en-bloc signals", and sends only a public building name
// (never finances). The Worker gathers via Firecrawl, scores, and persists it.
export interface EnblocInsightParams {
  project: string;
  district?: string | null;
  tenure?: string | null;
  units?: number | null;
  mrtMin?: number | null;
}
export async function fetchEnblocInsight(p: EnblocInsightParams): Promise<EnblocInsight> {
  const q = new URLSearchParams({ project: p.project });
  if (p.district) q.set("district", p.district);
  if (p.tenure) q.set("tenure", p.tenure);
  if (p.units) q.set("units", String(p.units));
  if (p.mrtMin) q.set("mrtMin", String(p.mrtMin));
  // The Worker fetches live news, so allow generous time but still bound it, so a
  // stalled crawl fails cleanly instead of hanging the button forever.
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), 30000) : null;
  try {
    const res = await fetch(`${API_BASE}/api/enbloc?${q.toString()}`, ctrl ? { signal: ctrl.signal } : undefined);
    if (!res.ok) throw new Error(`API ${res.status}`);
    const json = (await res.json()) as { insight: EnblocInsight };
    return json.insight;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Lightweight status probe — is the en-bloc news feed (Firecrawl) configured on
// the Worker? Sends no district/project. null on failure.
export async function fetchEnblocStatus(): Promise<{ configured: boolean } | null> {
  try {
    const res = await fetch(`${API_BASE}/api/enbloc-digest`);
    if (!res.ok) return null;
    const j = (await res.json()) as { configured?: boolean };
    return { configured: !!j.configured };
  } catch {
    return null;
  }
}

// Privacy-safe district digest — no project name sent, so it never reveals which
// unit the user is tracking. Returns null on failure so the UI degrades quietly.
export async function fetchEnblocDigest(district: string): Promise<EnblocDigest | null> {
  try {
    const res = await fetch(`${API_BASE}/api/enbloc-digest?district=${encodeURIComponent(district)}`);
    if (!res.ok) return null;
    return (await res.json()) as EnblocDigest;
  } catch {
    return null;
  }
}

export async function fetchRates(): Promise<Rates | null> {
  try {
    const res = await fetch(`${API_BASE}/api/rates`);
    if (!res.ok) return null;
    return (await res.json()) as Rates;
  } catch {
    return null;
  }
}

export interface Comp {
  month: string;
  town: string;
  flat_type: string;
  block: string;
  street: string;
  storey: string;
  area_sqm: number;
  remaining_lease: string;
  price: number;
}

// Distribution of a comparable set (recent transactions), used to place a
// listing against the market. `unit` is the rate basis: "psf" (private) or
// "psm" (HDB). `rates` is the full sorted rate array for percentile placement.
export interface CompStats {
  unit: "psf" | "psm";
  n: number;
  months: number;
  latestMonth: string | null;
  medianRate: number;
  p25Rate: number;
  p75Rate: number;
  minRate: number;
  maxRate: number;
  medianPrice: number;
  rates: number[];
}

export async function fetchComps(params: {
  town?: string;
  flatType?: string;
  maxPrice?: number;
  minArea?: number;
  minLease?: number;
  months?: number;
}): Promise<{ comps: Comp[]; stats: CompStats | null }> {
  const q = new URLSearchParams();
  if (params.town) q.set("town", params.town);
  if (params.flatType) q.set("flatType", params.flatType);
  if (params.maxPrice && Number.isFinite(params.maxPrice)) q.set("maxPrice", String(Math.round(params.maxPrice)));
  if (params.minArea) q.set("minArea", String(params.minArea));
  if (params.minLease) q.set("minLease", String(params.minLease));
  if (params.months) q.set("months", String(params.months));
  const res = await fetch(`${API_BASE}/api/comps?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  const json = (await res.json()) as { comps: Comp[]; stats?: CompStats };
  return { comps: json.comps ?? [], stats: json.stats ?? null };
}

export interface PrivateComp {
  month: string;
  district: string;
  project: string;
  street: string;
  property_type: string;
  area_sqm: number;
  price: number;
  psf: number;
  tenure?: string; // raw URA tenure string (exact value, shown on hover)
  freehold?: boolean;
  leaseLeft?: number | null; // remaining lease years at the transaction month
}

// Residential private property types (URA), for the type filter.
export const PRIVATE_TYPES = ["Condominium", "Executive Condominium", "Apartment", "Terrace", "Semi-Detached", "Detached"];

export interface PrivateFilters {
  district?: string;
  maxPrice?: number;
  maxPsf?: number;
  propertyType?: string;
  tenure?: "" | "freehold" | "leasehold";
  project?: string; // anchor comps to a specific development
  street?: string; // …or a specific street
}

export async function fetchPrivateComps(params: PrivateFilters): Promise<{ comps: PrivateComp[]; cached: number; stats: CompStats | null }> {
  const q = new URLSearchParams();
  if (params.district) q.set("district", params.district);
  if (params.maxPrice && Number.isFinite(params.maxPrice)) q.set("maxPrice", String(Math.round(params.maxPrice)));
  if (params.maxPsf && Number.isFinite(params.maxPsf)) q.set("maxPsf", String(Math.round(params.maxPsf)));
  if (params.propertyType) q.set("propertyType", params.propertyType);
  if (params.tenure) q.set("tenure", params.tenure);
  if (params.project) q.set("project", params.project);
  if (params.street) q.set("street", params.street);
  const res = await fetch(`${API_BASE}/api/private-comps?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  const json = (await res.json()) as { comps: PrivateComp[]; cached?: number; stats?: CompStats };
  return { comps: json.comps ?? [], cached: json.cached ?? 0, stats: json.stats ?? null };
}

export interface UnderValuedUnit {
  month: string;
  block: string;
  street: string;
  storey: string;
  area_sqm: number;
  remaining_lease: string;
  price: number;
  fairValue: number;
  pctBelow: number; // negative = below fair value
  belowBand?: boolean; // below the model's held-out P10
  outlier?: boolean; // excluded from the fit as a statistical outlier
}

// Held-out (out-of-sample) error band of a fitted segment model, as fractions
// of fair value: P10 at fair×(1+lowPct), P90 at fair×(1+highPct).
export interface ModelBand {
  lowPct: number;
  highPct: number;
  mapeOos: number;
}

export interface FairValueResponse {
  units: UnderValuedUnit[];
  n: number;
  r2: number;
  ok: boolean;
  band?: ModelBand;
  note?: string;
}

// Hedonic fair-value: under-valued HDB units within a town + flat type, from
// licensed transacted data only (no listing scraping).
export async function fetchUndervaluedHdb(town: string, flatType: string, filters: { maxPrice?: number; minLease?: number } = {}): Promise<FairValueResponse> {
  const q = new URLSearchParams();
  if (town) q.set("town", town);
  if (flatType) q.set("flatType", flatType);
  if (filters.maxPrice && Number.isFinite(filters.maxPrice)) q.set("maxPrice", String(Math.round(filters.maxPrice)));
  if (filters.minLease && filters.minLease > 0) q.set("minLease", String(filters.minLease));
  const res = await fetch(`${API_BASE}/api/undervalued?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  return (await res.json()) as FairValueResponse;
}

export interface UnderValuedPrivate {
  month: string;
  project: string;
  street: string;
  property_type: string;
  area_sqm: number;
  price: number;
  psf: number;
  tenure: string; // raw URA tenure string (exact value, shown on hover)
  freehold: boolean;
  leaseLeft: number | null; // remaining lease years at the transaction month
  fairValue: number;
  pctBelow: number;
  belowBand?: boolean;
  outlier?: boolean;
}

export interface PrivateFairValueResponse {
  units: UnderValuedPrivate[];
  n: number;
  r2: number;
  ok: boolean;
  projectFE: boolean;
  band?: ModelBand;
  note?: string;
}

// Under-valued private units within a district (URA transacted data, project
// fixed effects). Filters narrow the displayed results, not the model fit.
export async function fetchUndervaluedPrivate(district: string, filters: Omit<PrivateFilters, "district" | "maxPsf"> = {}): Promise<PrivateFairValueResponse> {
  const q = new URLSearchParams();
  if (district) q.set("district", district);
  if (filters.propertyType) q.set("propertyType", filters.propertyType);
  if (filters.tenure) q.set("tenure", filters.tenure);
  if (filters.maxPrice && Number.isFinite(filters.maxPrice)) q.set("maxPrice", String(Math.round(filters.maxPrice)));
  const res = await fetch(`${API_BASE}/api/undervalued-private?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  return (await res.json()) as PrivateFairValueResponse;
}

export interface EvalResult {
  fairValue: number; // P50
  p10?: number; // held-out 10th percentile of comparable outcomes
  p90?: number;
  mapeOos?: number; // held-out mean absolute % error
  nOos?: number;
  n: number;
  r2: number; // in-sample, for reference only
  ok: boolean;
  projectKnown?: boolean;
  imputed?: string[]; // blank inputs filled with the segment median
  outliers?: number; // rows screened out of the fit
  extrapolated?: string[]; // inputs outside the fitted range
  note?: string;
}

export async function fetchEvaluatePrivate(p: {
  district: string; areaSqm: number; tenure: "freehold" | "leasehold"; remainingLease?: number; propertyType?: string; project?: string; floor?: number;
}): Promise<EvalResult> {
  const q = new URLSearchParams({ district: p.district, areaSqm: String(p.areaSqm), tenure: p.tenure });
  if (p.remainingLease) q.set("remainingLease", String(p.remainingLease));
  if (p.floor) q.set("floor", String(p.floor));
  if (p.propertyType) q.set("propertyType", p.propertyType);
  if (p.project) q.set("project", p.project);
  const res = await fetch(`${API_BASE}/api/evaluate-private?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  return (await res.json()) as EvalResult;
}

// Nearest MRT/LRT + resolved address for a project name or address.
export interface NearestMrt {
  name: string;
  code?: string;
  line?: string;
  distanceM: number; // walking-distance estimate (metres)
  walkMin: number; // walking-time estimate (minutes)
}
export interface LocateResult {
  found: boolean;
  address?: string;
  name?: string;
  lat?: number;
  lng?: number;
  postal?: string | null;
  nearestMrt?: NearestMrt | null;
  mrtConfigured?: boolean;
  error?: string;
}

// Geocode a project name / address and find the nearest MRT (OneMap). Used by
// the listing evaluator to score transit access for buyers who don't drive.
export async function fetchLocate(q: string, opts?: { street?: string }): Promise<LocateResult> {
  const params = new URLSearchParams({ q });
  // Fallback for when a marketed project name misses OneMap's gazetteer.
  if (opts?.street && opts.street.trim()) params.set("street", opts.street.trim());
  const res = await fetch(`${API_BASE}/api/locate?${params.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  return (await res.json()) as LocateResult;
}

export async function fetchEvaluateHdb(p: {
  town: string; flatType: string; areaSqm: number; storey?: number; remainingLease?: number;
}): Promise<EvalResult> {
  const q = new URLSearchParams({ town: p.town, flatType: p.flatType, areaSqm: String(p.areaSqm) });
  if (p.storey) q.set("storey", String(p.storey));
  if (p.remainingLease) q.set("remainingLease", String(p.remainingLease));
  const res = await fetch(`${API_BASE}/api/evaluate-hdb?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  return (await res.json()) as EvalResult;
}

// Longitudinal district price trends over accumulated URA transaction history.
export interface TrendPoint { month: string; n: number; psf: number }
export interface DistrictTrend {
  district: string;
  points: TrendPoint[]; // all months present (n >= 1)
  qualifying: number; // months with n >= minN
  changePct: number | null; // psf change over the qualifying window
  direction: "rising" | "cooling" | "flat" | null;
  slopePctPerMonth?: number | null; // least-squares slope ÷ mean psf (per month)
  trendZ?: number | null; // latest price vs its own multi-year trend (+ = stretched)
  trendReady: boolean;
}
export interface PrivateTrends {
  ready: boolean;
  monthsOfHistory: number;
  minMonth: string | null;
  maxMonth: string | null;
  totalTxns: number;
  minN: number;
  minMonths: number;
  readyDistricts: number;
  districts: DistrictTrend[];
  source: string;
}

export async function fetchPrivateTrends(): Promise<PrivateTrends | null> {
  try {
    const res = await fetch(`${API_BASE}/api/private-trends`);
    if (!res.ok) return null;
    return (await res.json()) as PrivateTrends;
  } catch {
    return null;
  }
}

// Developer clear-out watch — developments where the developer is under pressure
// to sell unsold stock (the setting for ABSD-deadline discounts). Built from
// URA's licensed developers'-sales data only: discount *pressure* + the
// developer's own median-price trend, never a scraped asking price.
export interface ClearoutProject {
  project: string;
  district: string;
  region: string;
  totalUnits: number | null;
  unsold: number;
  unsoldPct: number | null;
  soldCum: number | null;
  absorptionPerMonth: number | null;
  monthsToSellout: number | null;
  medianPrice: number | null;
  medianPsf: number | null;
  priceChangePct: number | null;
  snapshots: number;
  latestMonth: string;
  score: number | null;
  band: "high" | "moderate" | "low" | "insufficient";
  confidencePct: number;
  absdDeadline: null; // dating is a planned follow-up
  note: string;
}
export interface DeveloperClearout {
  generated_at: string;
  monthsOfHistory: number;
  latestMonth: string | null;
  summary: { projectsWithUnsold: number; totalUnsoldUnits: number; highPressure: number; topProject: string | null };
  projects: ClearoutProject[];
  source: string;
  note: string;
}

export async function fetchDeveloperClearout(params: { district?: string; region?: string; maxPrice?: number } = {}): Promise<DeveloperClearout | null> {
  try {
    const q = new URLSearchParams();
    if (params.district) q.set("district", params.district);
    if (params.region) q.set("region", params.region);
    if (params.maxPrice && Number.isFinite(params.maxPrice)) q.set("maxPrice", String(Math.round(params.maxPrice)));
    const res = await fetch(`${API_BASE}/api/developer-clearout?${q.toString()}`);
    if (!res.ok) return null;
    return (await res.json()) as DeveloperClearout;
  } catch {
    return null;
  }
}

export interface School {
  name: string;
  address: string;
  postal: string;
  area: string;
  level: string;
}

export async function fetchSchools(q: string): Promise<School[]> {
  const res = await fetch(`${API_BASE}/api/schools?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  const json = (await res.json()) as { schools: School[] };
  return json.schools ?? [];
}

export interface CommuteLeg {
  drive: number | null;
  pt: number | null;
}
export interface CommuteArea {
  area: string;
  legs: CommuteLeg[]; // aligned with destinations
}
export interface CommuteResult {
  destinations?: { label: string; name: string }[];
  areas?: CommuteArea[];
  error?: string;
  configured?: boolean;
}

export async function fetchHdbTownMedians(flatType: string): Promise<Record<string, number>> {
  const q = new URLSearchParams();
  if (flatType) q.set("flatType", flatType);
  const res = await fetch(`${API_BASE}/api/hdb-town-medians?${q.toString()}`);
  if (!res.ok) throw new Error(`API ${res.status}`);
  const json = (await res.json()) as { medians: Record<string, number> };
  return json.medians ?? {};
}

export async function fetchCommute(works: string[], school: string): Promise<CommuteResult> {
  const q = new URLSearchParams();
  if (works[0]) q.set("work", works[0]);
  if (works[1]) q.set("work2", works[1]);
  if (school) q.set("school", school);
  const res = await fetch(`${API_BASE}/api/commute?${q.toString()}`);
  return (await res.json()) as CommuteResult;
}

export const HDB_TOWNS = [
  "ANG MO KIO", "BEDOK", "BISHAN", "BUKIT BATOK", "BUKIT MERAH", "BUKIT PANJANG",
  "BUKIT TIMAH", "CENTRAL AREA", "CHOA CHU KANG", "CLEMENTI", "GEYLANG", "HOUGANG",
  "JURONG EAST", "JURONG WEST", "KALLANG/WHAMPOA", "MARINE PARADE", "PASIR RIS",
  "PUNGGOL", "QUEENSTOWN", "SEMBAWANG", "SENGKANG", "SERANGOON", "TAMPINES",
  "TOA PAYOH", "WOODLANDS", "YISHUN",
];

export const HDB_FLAT_TYPES = ["2 ROOM", "3 ROOM", "4 ROOM", "5 ROOM", "EXECUTIVE"];

export function apiConfigured(): boolean {
  return API_BASE.length > 0 || location.hostname === "localhost";
}
